import React, { useEffect, useState } from 'react'
import './GameSummary.css'
import { useParams } from 'react-router-dom'
import Footer from '../UIElements/Footer'

const GameSummary = () => {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState()
	const [loadedGame, setLoadedGame] = useState()
	const [loadedGameStats, setLoadedGameStats] = useState()
	const [winner, setWinner] = useState()

	const [homeRoster, setHomeRoster] = useState()
	const [visitorRoster, setVisitorRoster] = useState()
	const [rosterPlayerGameStats, setRosterPlayerGameStats] = useState()

	const { gameId } = useParams()

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/admin/game/${gameId}`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message || 'Could not load the game.')
				}

				setLoadedGame(responseData.game)
				setLoadedGameStats(responseData.gameStats)
				setWinner(responseData.winner)
			} catch (err) {
				setError(err.message)
			} finally {
				setIsLoading(false)
			}
		}

		sendRequest()
	}, [gameId])

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/admin/${gameId}/rostersAndPoints`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(
						responseData.message || 'Could not load the scoring summary.',
					)
				}

				setHomeRoster(responseData.homeRoster || [])
				setVisitorRoster(responseData.visitorRoster || [])

				setRosterPlayerGameStats(responseData.rosterPlayerGameStats || [])
			} catch (err) {
				setError(err.message)
			} finally {
				setIsLoading(false)
			}
		}

		sendRequest()
	}, [gameId])

	const allPlayers = [
		...(Array.isArray(homeRoster) ? homeRoster : []),
		...(Array.isArray(visitorRoster) ? visitorRoster : []),
	]

	const findPlayer = (playerId) => {
		return allPlayers.find((player) => player._id === playerId)
	}

	const getNumber = (playerId) => {
		return findPlayer(playerId)?.number ?? ''
	}

	const getPlayerName = (playerId) => {
		const player = findPlayer(playerId)

		if (!player) {
			return ''
		}

		return `${player.firstName || ''} ${player.lastName || ''}`.trim()
	}

	const getTeamName = (playerId) => {
		return findPlayer(playerId)?.teamName ?? ''
	}

	const formatGameDate = (game) => {
		if (!game?.date) {
			return ''
		}

		const monthNames = [
			'',
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		]

		const dateParts = game.date.split(/[-/]/)

		const month = Number(dateParts[0])
		const day = Number(dateParts[1])

		if (!monthNames[month] || !day) {
			return `${game.dayOfWeek || ''} ${game.date}`.trim()
		}

		return `${game.dayOfWeek || ''}, ${monthNames[month]} ${day}`.replace(
			/^,\s*/,
			'',
		)
	}

	const formatGameTime = (time) => {
		if (!time) {
			return ''
		}

		const [hours, minutes] = time.split(':').map(Number)

		const gameTime = new Date()
		gameTime.setHours(hours, minutes, 0, 0)

		return gameTime
			.toLocaleTimeString('en-US', {
				hour: 'numeric',
				minute: '2-digit',
			})
			.toLowerCase()
	}

	const getResultText = () => {
		if (!loadedGame) {
			return null
		}

		if (!winner) {
			return (
				<>
					Tie game. Final score:{' '}
					<span className='gameSummaryResultHighlight'>
						<span className='gameSummaryScore'>
							{loadedGame.score.replace(/\s*-\s*/, '\u00A0-\u00A0')}
						</span>
					</span>
				</>
			)
		}

		if (loadedGame.status === 'Overtime') {
			return (
				<>
					{winner} won in{' '}
					<span className='gameSummaryResultHighlight'>OVERTIME</span>, by a
					score of{' '}
					<span className='gameSummaryResultHighlight'>
						<span className='gameSummaryScore'>
							{loadedGame.score.replace(/\s*-\s*/, '\u00A0-\u00A0')}
						</span>
					</span>
				</>
			)
		}

		if (loadedGame.status === 'Shootout') {
			return (
				<>
					{winner} won in a{' '}
					<span className='gameSummaryResultHighlight'>SHOOTOUT</span>, by a
					score of{' '}
					<span className='gameSummaryResultHighlight'>
						<span className='gameSummaryScore'>
							{loadedGame.score.replace(/\s*-\s*/, '\u00A0-\u00A0')}
						</span>
					</span>
				</>
			)
		}

		return (
			<>
				{winner} won by a score of{' '}
				<span className='gameSummaryResultHighlight'>
					<span className='gameSummaryScore'>
						{loadedGame.score.replace(/\s*-\s*/, '\u00A0-\u00A0')}
					</span>
				</span>
			</>
		)
	}

	return (
		<div className='gameSummaryPage'>
			{error && (
				<p className='gameSummaryError'>
					{typeof error === 'string'
						? error
						: 'The game summary could not be loaded.'}
				</p>
			)}

			{isLoading && (
				<div className='center'>
					<p>Loading...</p>
				</div>
			)}

			{!isLoading && loadedGame && (
				<main className='Summary'>
					<section className='gameSummaryHeroCard'>
						<div className='gameSummaryMatchup'>
							<div className='gameSummaryVisitor'>
								{loadedGame.visitorTeamName}
							</div>

							<div className='gameSummaryAtSymbol'>@</div>

							<div className='gameSummaryHome'>{loadedGame.homeTeamName}</div>
						</div>

						<div className='gameSummaryAccent'></div>

						<p className='gameSummaryDateTime'>
							{formatGameDate(loadedGame)} at {formatGameTime(loadedGame.time)}
						</p>

						<div className='gameSummaryDivider'></div>

						<h2 className='gameSummaryResult'>{getResultText()}</h2>

						{loadedGameStats?.summary && (
							<>
								<div className='gameSummaryDivider'></div>

								<p className='gameRecap'>{loadedGameStats.summary}</p>
							</>
						)}
					</section>

					{homeRoster && visitorRoster && rosterPlayerGameStats && (
						<section className='ScoringSummary'>
							<h2 className='scoringSummaryTitle'>Scoring Summary</h2>

							<div className='scoringSummaryAccent'></div>

							<div className='scoringSummaryTableWrapper'>
								<table id='scoringSummary'>
									<thead>
										<tr>
											<th>#</th>
											<th>Player</th>
											<th>Team</th>
											<th>Goals</th>
											<th>Assists</th>
										</tr>
									</thead>

									<tbody>
										{rosterPlayerGameStats.map((player, key) => (
											<tr key={player._id || player.rosterPlayerId || key}>
												<td>{getNumber(player.rosterPlayerId)}</td>

												<td className='scoringSummaryPlayer'>
													{getPlayerName(player.rosterPlayerId)}
												</td>

												<td className='scoringSummaryTeam'>
													{getTeamName(player.rosterPlayerId)}
												</td>

												<td>{Number(player.goals || 0)}</td>

												<td>{Number(player.assists || 0)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{rosterPlayerGameStats.length === 0 && (
								<p className='scoringSummaryEmpty'>
									No player scoring was recorded for this game.
								</p>
							)}
						</section>
					)}
				</main>
			)}

			<Footer />
		</div>
	)
}

export default GameSummary
